import { Suspense } from 'react';
import { RouterProvider } from 'react-router/dom';
import { router } from './router';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AppProvider } from '@contexts/app';
import { WebsocketProvider } from '@contexts/websocket';
import posthog from 'posthog-js';
import { PostHogProvider } from '@posthog/react';
import {
  isFirewallChallenge,
  isNotifiedGlobally,
  isSilentError,
  notifyQueryError,
  reportUnexpectedError,
  retryNetworkError,
} from '@services/queryErrors';

export const App = () => {
  const queryClient = new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        reportUnexpectedError(error, 'query', query.queryKey);
        if (query.meta?.silentError || isSilentError(error) || !isNotifiedGlobally(error)) return;
        notifyQueryError(error);
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        reportUnexpectedError(error, 'mutation', mutation.options.mutationKey);
        if (isFirewallChallenge(error))
          notifyQueryError(error, 'messages.failure.request.blockedSave');
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 2 * 1000,
        retry: retryNetworkError,
        refetchOnMount: 'always',
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
      },
    },
  });

  return (
    <QueryClientProvider client={queryClient}>
      <PostHogProvider client={posthog}>
        <AppProvider>
          <WebsocketProvider>
            <Suspense>
              <RouterProvider router={router} />
            </Suspense>
            <ReactQueryDevtools initialIsOpen={false} />
          </WebsocketProvider>
        </AppProvider>
      </PostHogProvider>
    </QueryClientProvider>
  );
};
